import { Component, OnDestroy } from '@angular/core';

import { Subscription, forkJoin, of, Subject, Observable } from 'rxjs';
import { filter, finalize, map, takeUntil, switchMap, catchError, tap } from 'rxjs/operators';
import { groupBy } from 'lodash';
import moment from 'moment';

import { MentionsApi } from '@api/mentions/mentions.api';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';

import { AnswerEngineOption, getAllEngineOptions } from '../../models/answer-engine-option.model';
import { AttributeModel } from '@Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '@Common/Misc/enums/attribute-types.enum';
import { ViMonitorService } from '../../services/vi-monitor.service';
import { ViMonitor } from '../../models/vi-monitor.model';
import { JournodexSearchesApi } from '@api/targeting/journodex-searches.api';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { ViAnalysisEnum } from '../../enums/vi-analysis.enum';
import { ViBrandIdentity } from '../../models/vi-brand-identity.model';

import { ViInsightPrompt } from '../../models/vi-insight-prompt.model';

import { ViQuestionApi } from '@api/visibilityIntelligence/models/vi-question.api.model';
import { ViDimensionEnum } from '../../enums/vi-dimension.enum';
import { viApiToEngineType } from '../../helpers/vi-engine-type.map';

import { MentionModel } from '@api/mentions/model/mention.model';
import { OutletsPublicApi } from '@api/targeting/outlets-public.api';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { AnswerEngineTypesEnum } from '../../enums/answer-engine-types.enum';
import { OutletModel } from '@Discover/Targeting/models/outlet.model';
import { ViSourceOutlet } from '../../models/vi-source-outlet.model';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';
import { ViAnswerEngineSource } from '../../models/vi-answer-engine-source.model';
import { SearchContactsAndOutletsTypeApiEnum } from '@api/targeting/enum/search-contacts-and-outlets-type.enum';
import { ViDimensionCard } from '../../models/vi-dimension-card.model';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';
import { bcp47LanguageMap } from '@Common/Forms/data/bcp47-language-tags';
import { ViAnswerEngineApiEnum } from '@api/visibilityIntelligence/enums/vi-answer-engine.api.enum';
import { ViApiService } from '@api/visibilityIntelligence/vi.api.service';
import { ViRunRequestApi } from '@api/visibilityIntelligence/models/vi-run-request.api.model';
import { DateHelper, DayKey } from '@Common/Forms/helpers/date.helper';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ViMonitorStatusEnum } from '../../enums/vi-monitor-status.enum';
import { isBlockedHost } from '../../helpers/vi-outlet-url-block-list';

enum ViAttributeType {
    NAME,
    ANSWER_ENGINES,
    ANALYSIS_FOCUS,
    LANGUAGE,
    COMPETITOR_BENCHMARKING,
    CREATED_BY,
    CREATED_DATE
}

// we have to convert all the run times to local time first, because the date picker uses Date().. which automatically converts your time to
// local time.. so when you click on a date.. it will be (ex: oct 22, 00:00:00 local time)
// so the dates can be a bit off depending
const toRunDay = (iso: string): DayKey => {
    // // Bucket by UTC day: dateChanged() treats the picked calendar day as a UTC day (see toUtcDate),
    // // so converting to local here would shift runs to the next/previous day in non-UTC timezones.
    // return moment.utc(iso).format('YYYY-MM-DD');
    // 1. Parse the string as a UTC time
    // 2. Convert it to the user's local time zone and format it
    return moment.utc(iso).format('YYYY-MM-DD');
};

/**
 * Strange Behaviour: Fetching engine results is done via mention searches.
 */
@Component({
    selector: 'agpr-vi-queries-and-answers',
    templateUrl: './vi-queries-and-answers.component.html',
    styleUrl: './vi-queries-and-answers.component.scss',
    standalone: false
})
export class ViQueriesAndAnswersComponent implements OnDestroy {

    currentMonitor: ViMonitor;

    dayOptions: DayKey[] = [];
    selectedDate: Date;
    previousDate: Date;
    mostRecentDate: Date;

    selectedAnswerEngine: AnswerEngineOption;


    selectedQuestion: ViQuestionApi;
    dimensionCards: ViDimensionCard[] = Object.values(ViDimensionEnum).map(x =>
        ({
            dimension: x as ViDimensionEnum,
            questions: [],
            isExpanded: true
        })
    );

    loading: boolean = true;
    rhpLoading: boolean = false;
    hasData: boolean = true;

    brand: ViBrandIdentity;
    competitors: ViBrandIdentity[] = [];
    selectedInsightsLanguage: string = '';
    executedPrompt: string = '';
    timezone: string;

    answerEngineOptions: AnswerEngineOption[] = [];
    previousAnswerEngineOptions: AnswerEngineOption[] = [];
    selectedEngineType: AnswerEngineTypesEnum;

    outletList: ViSourceOutlet[] = [];
    outletListLoading: boolean = false;

    attributes: AttributeModel[] = [];


    private readonly searchDestroy$ = new Subject<void>();

    readonly id = 'vi-QnA';
    readonly subscriptions: Subscription[] = [];
    readonly monitorStatus = ViMonitorStatusEnum;
    readonly messageSeverities = messageSeverityEnum;

    readonly prompts: ViInsightPrompt[] = [
        {
            analysisType: ViAnalysisEnum.SENTIMENT_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.REPRESENTATION_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.COMPETITIVE_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.CITATION_AUTHORITY_ANALYSIS,
            expanded: false,
            aiResponse: ''
        },
        {
            analysisType: ViAnalysisEnum.CUSTOM,
            isCustomPrompt: true,
            expanded: false,
            aiResponse: ''
        }
    ];

    readonly engineLabelMap: Record<ViAnswerEngineApiEnum, string> = {
        [ViAnswerEngineApiEnum.openai]: 'OpenAI',
        [ViAnswerEngineApiEnum.gemini]: 'Gemini',
        [ViAnswerEngineApiEnum.perplexity]: 'Perplexity',
        [ViAnswerEngineApiEnum.xai]: 'Grok',
        [ViAnswerEngineApiEnum.anthropic]: 'Claude',
    };

    constructor(
        private viMonitorService: ViMonitorService,
        private localizationService: LocalizationService,
        private outletsPublicApi: OutletsPublicApi,
        private journodexSearchesApi: JournodexSearchesApi,
        private viApiService: ViApiService,
        private mentionsApi: MentionsApi,
    ) {
        this.subscriptions.push(
            this.viMonitorService.currentResource$.pipe(
                filter(x => !!x),
                tap(monitor => {
                    this.loading = false;
                    this.currentMonitor = monitor;
                    this.brand = this.currentMonitor.brand;
                    this.competitors = this.currentMonitor.competitors ?? [];

                    this.attributes = this.getAttributes(this.currentMonitor);
                    this.answerEngineOptions = this.getEngineOptions(this.currentMonitor.answerEngines);
                    this.dimensionCards = this.getDimensionCards(this.currentMonitor.questions);

                    this.selectedEngineType = this.answerEngineOptions[0]?.type;
                }),
                switchMap(() => {
                    const request: ViRunRequestApi = {
                        start: this.currentMonitor.createdDate.getUTCMilliseconds(),
                    };
                    return this.viApiService.getRuns(this.currentMonitor.id, request);
                })
            ).subscribe(runs => {
                // we have to convert all the run times to local time first, because the date picker uses Date().. which automatically converts your time to
                // local time.. so when you click on a date.. it will be (ex: oct 22, 00:00:00 local time)
                // so the dates can be a bit off depending
                this.dayOptions = [...new Set(runs.map(x => toRunDay(x.publishedDate)))]
                    .sort()
                    .reverse();

                // A run record existing doesn't mean it produced any answers yet; hasData is set once the actual search resolves.
                if (this.dayOptions.length > 0) {
                    this.mostRecentDate = this.toUtcDate(DateHelper.fromDayKey(this.dayOptions[0]));
                    this.selectedDate = DateHelper.fromDayKey(this.dayOptions[0]);
                    this.selectedDate.setHours(23, 59, 59, 999);
                    this.previousDate = this.toUtcDate(this.getPreviousDay(this.selectedDate));
                } else {
                    this.hasData = false;
                    this.mostRecentDate = null;
                    this.selectedDate = null;
                    this.previousDate = null;
                }

                // @TODO does initial search regardless if there are results, only perform search when run dayOptions are available
                this.focusQuestion(this.dimensionCards.find(card => card.questions.length > 0)?.questions[0]);
            })
        );
    }


    dateChanged() {
        this.previousDate = this.getPreviousDay(this.selectedDate);
        this.loadAllAnswerEngineResults();
    }

    /**
     * Returns a Date whose UTC wall-clock date/time matches the local wall-clock of `value`.
     * e.g. May 26 00:00 local (Toronto) -> 2026-05-26T00:00:00Z
     */
    private toUtcDate(value: Date | string): Date {
        const parsed = moment(value);

        // Build from components (not by adding minutes) so DST transitions can't shift the result.
        const date = new Date(Date.UTC(
            parsed.year(), parsed.month(), parsed.date(),
            parsed.hours(), parsed.minutes(), parsed.seconds(), parsed.milliseconds()
        ));
        return date;
    }

    focusQuestion(selectedQuestion: ViQuestionApi) {
        if (!selectedQuestion) {
            return;
        }

        this.selectedQuestion = selectedQuestion;
        this.loadAllAnswerEngineResults();
    }


    private loadAllAnswerEngineResults() {
        this.searchDestroy$.next();
        this.outletListLoading = true;

        this.answerEngineOptions.forEach(option => {
            option.loading = true;
            option.answerText = '';
            option.sources = [];
            option.rawResponse = undefined;
        });

        this.prompts.forEach(prompt => {
            if (prompt.destroyApiCall$) {
                prompt.destroyApiCall$.next();
            }
            if (prompt.apiCall) {
                prompt.apiCall.unsubscribe();
            }
            prompt.aiResponse = '';
            prompt.generating = false;
            prompt.responseHtml = '';
            prompt.expanded = false;
        });

        const criteria = new MentionSearchCriteriaModel(undefined, true);
        criteria.isMonitorSearch = true;
        // @HACK weird b/e LLM_RESPONSE is a MediaTypeEnum
        criteria.query.includedMediaTypes = [new MediaTypeModel('LLM_RESPONSE' as MediaTypeEnum)];
        criteria.query.aiAgent.include = [this.currentMonitor.agentId];

        if (this.selectedQuestion?.id) {
            criteria.query.aiAgentSource.include = [+this.selectedQuestion.id];
        }

        if (this.selectedDate) {
            const dateQuery = new DateQueryModel();
            dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
            dateQuery.timezone = 'UTC';
            const wholeEndDay = new Date(this.selectedDate);
            wholeEndDay.setHours(23, 59, 59, 999);
            if (this.previousDate) {
                dateQuery.absoluteRange = {
                    start: this.previousDate,
                    end: this.toUtcDate(wholeEndDay)
                };
            } else {
                const wholeStartDay = new Date(this.selectedDate);
                wholeStartDay.setHours(0, 0, 0, 0);
                dateQuery.absoluteRange = {
                    start: this.toUtcDate(wholeStartDay),
                    end: this.toUtcDate(wholeEndDay)
                };
            }

            this.searchForAnsers(criteria, dateQuery);
        }
    }


    /**
     * Mentions the source produced over the sample window. Returns null on failure so the banner falls
     * back to "N/A" rather than showing a number we cannot stand behind.
     */
    private searchForAnsers(criteria: MentionSearchCriteriaModel, dateQuery: DateQueryModel) {
        if (!dateQuery || !dateQuery) {
            return;
        }

        criteria.query.dateQuery = dateQuery;
        return this.mentionsApi.viSearchAccount(criteria.getApiEntity()).pipe(
            takeUntil(this.searchDestroy$),
            switchMap((response: MentionsSearchResponseModel) => {
                const filteredMentions: MentionModel[] = response.mentions.filter(mention => {
                    return moment.unix((mention.publishedDate as number)/ 1000).utc().format('YYYY-MM-DD') === DateHelper.toDayKey(this.selectedDate);
                });

                this.applyPreviousResults(response.mentions);
                return this.resolveOutletsByHost(filteredMentions).pipe(
                    map(outletsByHost => ({ filteredMentions, outletsByHost }))
                );
            }),
            finalize(() => this.outletListLoading = false)
        ).subscribe({
            next: ({ filteredMentions, outletsByHost }) => this.applyResults(filteredMentions, outletsByHost),
            error: () => {
                this.answerEngineOptions.forEach(e => {
                    e.answerText = '';
                    e.sources = [];
                    e.loading = false;
                });
                this.outletList = [];
            }
        });
    }

    private applyResults(mentions: MentionModel[], outletsByHost: Map<string, OutletModel>) {
        this.hasData = mentions.length > 0;

        // preserves insertion order
        const outletInfoById = new Map<string, ViSourceOutlet>();

        // Assumes a single mention per engine per date; if an engine had more than one, whichever comes last here silently wins.
        mentions.forEach(({ viMetadata, body }) => {
            const type = viApiToEngineType[viMetadata.answerEngine];
            const engine = this.answerEngineOptions.find(e => e.type === type);

            if (!engine) {
                return;
            }

            // An engine can cite the same domain twice, so dedupe per engine
            const uniqueHosts = [...new Set(
                (viMetadata.citedSources ?? [])
                    .map(source => this.getLookupHost(source))
                    .filter((host): host is string => !!host)
            )];

            const sources: ViAnswerEngineSource[] = uniqueHosts.map(host => {
                const outlet = outletsByHost.get(host) ?? null;

                if (outlet) {
                    const existing = outletInfoById.get(outlet.id);
                    if (existing) {
                        if (!existing.answerEngines.includes(type)) {
                            existing.answerEngines.push(type);
                        }
                    } else {
                        outletInfoById.set(outlet.id, {
                            outlet,
                            circulation: outlet.circulations?.find(ci => ci.isSimilarWeb),
                            answerEngines: [type]
                        });
                    }
                }

                return {
                    link: `https://${host}`,
                    outletId: outlet?.id,
                    outlet
                } as ViAnswerEngineSource;
            });

            engine.answerText = body;
            engine.sources = engine.answerText ? sources : [];
            engine.rawResponse = {
                ...viMetadata,
                text: engine.answerText,
                citations: viMetadata.citedSources.map(url => ({
                    author: '',
                    icon: '',
                    title: '',
                    url: url
                })),
                answerEngine: this.engineLabelMap[viMetadata.answerEngine]
            };

            engine.loading = false;
        });

        // Engines missing from the response
        this.answerEngineOptions
            .filter(e => e.loading)
            .forEach(e => {
                e.answerText = '';
                e.sources = [];
                e.loading = false;
            });

        this.outletList = [...outletInfoById.values()];

        if (!this.selectedEngineType) {
            this.selectedEngineType = this.answerEngineOptions.find(e => e.answerText)?.type;
        }
    }

    private getPreviousDay(selected: Date): Date | undefined {
        if (!selected) {
            return undefined;
        }

        const selectedKey = DateHelper.toDayKey(selected);
        // Options are newest-first, so the first earlier day is the closest one.
        const previousDayKey = this.dayOptions.find(key => key < selectedKey);

        return previousDayKey ? DateHelper.fromDayKey(previousDayKey) : undefined;
    }

    private applyPreviousResults(mentions: MentionModel[]) {

        const previousEngineOptions: AnswerEngineOption[] = this.getEngineOptions(this.currentMonitor.answerEngines ?? [])
            .map(engine => ({ ...engine, rawResponse: undefined }));

        if (this.previousDate) {
            // Compare by day, not exact timestamp: previousDate is midnight of that day, but mentions carry a real time-of-day.
            // Assumes a single mention per engine per date; if an engine had more than one, whichever comes last here silently wins.
            mentions
                .filter(mention => {
                    return moment.unix((mention.publishedDate as number)/ 1000).utc().format('YYYY-MM-DD') ===
                        DateHelper.toDayKey(this.previousDate);
                })
                .forEach(({ viMetadata }) => {
                    if (!viMetadata?.answerEngine) {
                        return;
                    }
                    const type = viApiToEngineType[viMetadata.answerEngine];
                    const engine = previousEngineOptions.find(e => e.type === type);
                    if (engine) {
                        engine.rawResponse = {
                            ...viMetadata,
                            text: engine.answerText,
                            citations: viMetadata.citedSources.map(url => ({
                                author: '',
                                icon: '',
                                title: '',
                                url: url
                            })),
                            answerEngine: this.engineLabelMap[viMetadata.answerEngine]
                        };
                    }
                });
        }

        this.previousAnswerEngineOptions = previousEngineOptions;
    }


    /**
     * Collect a map of all url sources, fetching the OutletModel only once.
     * @Returns host -> OutletModel map
     */
    private resolveOutletsByHost(mentions: MentionModel[]): Observable<Map<string, OutletModel>> {

        const hosts = new Set<string>(mentions
            .flatMap(mention => mention.viMetadata.citedSources ?? [])
            .map(url => this.getLookupHost(url))
            .filter((host): host is string => !!host && !isBlockedHost(host))
        );

        if (!hosts.size) {
            return of(new Map());
        }

        const hostList = [...hosts];

        return forkJoin(hostList.map(host => this.findOutletIdByHost(host))).pipe(
            switchMap(outletIds => {
                const hostToId = new Map<string, string>();
                hostList.forEach((host, i) => {
                    if (outletIds[i]) {
                        hostToId.set(host, outletIds[i]);
                    }
                });

                const uniqueIds = [...new Set(hostToId.values())];
                if (!uniqueIds.length) {
                    return of(new Map<string, OutletModel>());
                }

                return forkJoin(uniqueIds.map(id =>
                    this.outletsPublicApi.getOutlet(id).pipe(
                        map(outlet => outlet ? new OutletModel(outlet) : null),
                        catchError(() => of(null))
                    )
                )).pipe(
                    map(outlets => {
                        const idToOutlet = new Map<string, OutletModel>();
                        uniqueIds.forEach((id, i) => {
                            if (outlets[i]) {
                                idToOutlet.set(id, outlets[i]);
                            }
                        });

                        const hostToOutlet = new Map<string, OutletModel>();
                        hostToId.forEach((id, host) => {
                            const outlet = idToOutlet.get(id);
                            if (outlet) {
                                hostToOutlet.set(host, outlet);
                            }
                        });
                        return hostToOutlet;
                    })
                );
            })
        );
    }

    private findOutletIdByHost(host: string): Observable<string | undefined> {
        return this.journodexSearchesApi.searchContactsAndOutlets({
            type: SearchContactsAndOutletsTypeApiEnum.outlet,
            url: host
        }).pipe(
            map(outlets => outlets[0]?.uuid),
            catchError(() => of(undefined)) // one failed lookup shouldn't kill the forkJoin
        );
    }

    /** Accepts full URLs or bare domains. Null for empty or unparseable values. */
    private getLookupHost(source?: string): string | null {
        const value = source?.trim();
        if (!value) {
            return null;
        }
        try {
            const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
            return new URL(withScheme).host.toLowerCase();
        } catch {
            return null;
        }
    }

    private getDimensionCards(questions: ViQuestionApi[]): ViDimensionCard[] {
        const grouped = groupBy(questions, x => x.dimension);

        return Object.values(ViDimensionEnum).map(dimension => ({
            dimension,
            questions: grouped[dimension] ?? [],
            isExpanded: true,
        }));
    }

    private getEngineOptions(engineTypes: AnswerEngineTypesEnum[]): AnswerEngineOption[] {
        if (!engineTypes) {

            return [];
        }

        const engineOptions: AnswerEngineOption[] = getAllEngineOptions();

        // getAllEngineOptions() returns shared instances. Clone them and reset their state to
        // prevent data from previous monitors from leaking into the current one.
        return engineOptions
            .filter(x => engineTypes.includes(x.type))
            .map(engine => ({
                ...engine,
                loading: true,
                answerText: '',
                sources: [],
                rawResponse: undefined,
                destroyApiCall$: new Subject<void>()
            }));
    }


    private getAttributes(monitor: ViMonitor): AttributeModel[] {
        if (!monitor) {

            return;
        }

        const nameAttribute: AttributeModel = {
            id: ViAttributeType.NAME,
            label: 'name',
            value: monitor.name,
            type: attributeTypesEnum.simple
        };

        const answerEnginesAttribute: AttributeModel = {
            id: ViAttributeType.ANSWER_ENGINES,
            label: 'Vi.answerEngines',
            value: monitor.answerEngines.map(engine => {
                return this.localizationService.transform(`Vi.answerEngineType.${engine}`);
            }).join(', '),
            type: attributeTypesEnum.simple
        };

        const analysisFocusAttribute: AttributeModel = {
            id: ViAttributeType.ANALYSIS_FOCUS,
            label: 'Vi.analysisFocus',
            value: monitor.analysisFocus.map(value => {
                return this.localizationService.transform(value);
            }).join(', '),
            type: attributeTypesEnum.simple
        };

        const outputLangAttribute: AttributeModel = {
            id: ViAttributeType.LANGUAGE,
            label: 'Vi.language',
            value: bcp47LanguageMap[monitor.language],
            type: attributeTypesEnum.simple
        };

        const compBenchmarkAttribute: AttributeModel = {
            id: ViAttributeType.COMPETITOR_BENCHMARKING,
            label: 'Vi.competitorBench',
            value: monitor.isCompetitiveBenchmarking ? 'enabled' : 'disabled',
            type: attributeTypesEnum.simple
        };

        const createdByAttribute: AttributeModel = {
            id: ViAttributeType.CREATED_BY,
            label: 'createdBy',
            value: monitor.createdByUserId,
            type: attributeTypesEnum.user
        };

        const createdDateAttribute: AttributeModel = {
            id: ViAttributeType.CREATED_DATE,
            label: 'createdDate',
            value: monitor.createdDate,
            type: attributeTypesEnum.date
        };

        const attributes = [
            nameAttribute,
            answerEnginesAttribute,
            analysisFocusAttribute,
            outputLangAttribute,
            compBenchmarkAttribute,
            createdByAttribute,
            createdDateAttribute
        ].filter(x => !!x.value);

        return attributes;
    }


    ngOnDestroy() {
        this.searchDestroy$.complete();
    }
}
